/**
 * Owns: delivery ZIP check dialog and inline alternate-store results for out-of-area ZIPs.
 * Exposes: delivery-zip-modal custom element, DELIVERY_ZIP_CHECKED_EVENT.
 * Consumes: store-events, store-inventory, store-product-availability, store-pricing, store-locator-geocoding;
 *   duck-types store-locator methods for alternate store search/render.
 */

import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { canAddToCartForDelivery, getStoreProductAvailability } from '@theme/store-inventory';
import { asStoreProductAvailability } from '@theme/store-product-availability';
import { normalizeZipDigits } from '@theme/store-locator-geocoding';
import {
  buildDeliveryZipPatch,
  fetchCartAttributes,
  findStoreFromCartAttributes,
  getDeliveryZipFromCartAttributes,
  hasStoreCartAttributes,
  isZipInStoreServiceArea,
  loadStoreCatalog,
  normalizeServiceZip,
} from '@theme/store-pricing';
import { patchCartAttributes } from '@theme/store-locator-cart';
import { DELIVERY_ZIP_CHECKED_EVENT } from '@theme/store-events';

export { DELIVERY_ZIP_CHECKED_EVENT };

/**
 * @typedef {Object} DeliveryZipCheckedDetail
 * @property {string} zip
 * @property {boolean} inServiceArea
 */

/**
 * @typedef {Object} AlternateStoreResult
 * @property {import('@theme/store-locator-search').StoreLocatorStore[]} stores
 * @property {Record<string, number>} distancesByStoreId
 * @property {import('@theme/store-locator-search').StoreLocatorSearchOrigin | null} origin
 */

/**
 * @typedef {HTMLElement & {
 *   findAlternateDeliveryStores: (zip: string, options?: { signal?: AbortSignal; limit?: number }) => Promise<AlternateStoreResult>;
 *   selectStoreForPdp: (store: import('@theme/store-locator-search').StoreLocatorStore) => Promise<{ ok: boolean }>;
 *   renderAlternateDeliveryResults: (
 *     stores: import('@theme/store-locator-search').StoreLocatorStore[],
 *     options?: {
 *       distancesByStoreId?: Record<string, number>;
 *       origin?: import('@theme/store-locator-search').StoreLocatorSearchOrigin | null;
 *       searchQuery?: string;
 *       onSelectStore?: (store: import('@theme/store-locator-search').StoreLocatorStore, button: HTMLButtonElement) => void;
 *     }
 *   ) => HTMLElement[];
 *   openWithQuery?: (query: string) => void;
 * }} StoreLocatorElement
 */

/**
 * @extends {Component}
 */
class DeliveryZipModal extends Component {
  /** @type {string} */
  #lastCheckedZip = '';

  /** @type {AbortController | null} */
  #alternateStoresAbort = null;

  /** @type {number} */
  #alternateStoresRequestId = 0;

  /**
   * @param {string} name
   * @returns {HTMLElement | null}
   */
  #getModalRef(name) {
    return this.querySelector(`#delivery-zip-modal [ref="${name}"]`);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDialog() {
    return this.querySelector('.delivery-zip-modal__dialog');
  }

  /**
   * @returns {StoreLocatorElement | null}
   */
  #getStoreLocator() {
    const locator = /** @type {StoreLocatorElement | null} */ (
      document.querySelector('store-locator')
    );
    if (
      locator &&
      typeof locator.findAlternateDeliveryStores === 'function' &&
      typeof locator.selectStoreForPdp === 'function' &&
      typeof locator.renderAlternateDeliveryResults === 'function'
    ) {
      return locator;
    }
    return null;
  }

  /**
   * @returns {import('@theme/store-product-availability').StoreProductAvailabilityElement | null}
   */
  #getStoreProductAvailability() {
    const productId = this.dataset.productId?.trim();
    return asStoreProductAvailability(getStoreProductAvailability(productId));
  }

  #abortAlternateStoresFetch() {
    this.#alternateStoresAbort?.abort();
    this.#alternateStoresAbort = null;
  }

  /**
   * @param {boolean} isLoading
   */
  #setCheckLoading(isLoading) {
    const checkButton = this.#getModalRef('checkButton');
    const checkLoading = this.#getModalRef('checkLoading');
    const zipInput = this.#getModalRef('zipInput');

    if (checkButton instanceof HTMLButtonElement) {
      checkButton.disabled = isLoading;
      checkButton.hidden = isLoading;
    }

    if (checkLoading instanceof HTMLElement) {
      checkLoading.hidden = !isLoading;
      checkLoading.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    if (zipInput instanceof HTMLInputElement) {
      zipInput.disabled = isLoading;
    }
  }

  #resetAlternateStoresUi() {
    const loading = this.#getModalRef('alternateStoresLoading');
    const empty = this.#getModalRef('alternateStoresEmpty');
    const alternateResults = this.#getModalRef('alternateResults');
    const count = this.#getModalRef('alternateStoresCount');
    const list = this.#getModalRef('alternateStoresList');
    const dialog = this.#getDialog();

    if (loading instanceof HTMLElement) loading.hidden = true;
    if (empty instanceof HTMLElement) empty.hidden = true;
    if (count instanceof HTMLElement) {
      count.hidden = true;
      count.textContent = '';
    }

    if (list instanceof HTMLElement) {
      list.replaceChildren();
    }

    if (alternateResults instanceof HTMLElement) {
      alternateResults.hidden = true;
    }

    dialog?.classList.remove('delivery-zip-modal__dialog--wide');
  }

  #hideUnavailableState() {
    this.#abortAlternateStoresFetch();
    this.#resetAlternateStoresUi();

    const unavailableResult = this.#getModalRef('unavailableResult');
    if (unavailableResult instanceof HTMLElement) {
      unavailableResult.hidden = true;
    }
  }

  #resetToForm() {
    this.#hideUnavailableState();
    this.#lastCheckedZip = '';
    this.#syncRemoveZipButton('');
  }

  /**
   * @param {string} zip
   */
  #syncRemoveZipButton(zip) {
    const removeButton = this.#getModalRef('removeZipButton');
    if (!(removeButton instanceof HTMLButtonElement)) return;

    removeButton.hidden = !zip.trim();
  }

  /**
   * @param {string} zip
   * @param {'loading' | 'withAlternates' | 'noAlternates'} mode
   */
  #setUnavailableMessage(zip, mode) {
    const unavailableMessage = this.#getModalRef('unavailableMessage');
    if (!(unavailableMessage instanceof HTMLElement)) return;

    if (mode === 'loading') {
      unavailableMessage.textContent = '';
      unavailableMessage.hidden = true;
      return;
    }

    const translations = Theme.translations ?? {};
    let template = '';

    if (mode === 'withAlternates') {
      template =
        translations.delivery_zip_modal_unavailable_with_alternates ??
        'Your store cannot deliver to ZIP code {{ zip }}. Select a store below for delivery.';
    } else {
      template =
        translations.delivery_zip_modal_unavailable_current_store ??
        translations.delivery_zip_modal_unavailable ??
        'Your store cannot deliver to ZIP code {{ zip }}.';
    }

    unavailableMessage.textContent = template.replace(/\{\{\s*zip\s*\}\}/g, zip);
    unavailableMessage.hidden = false;
  }

  /**
   * @param {string} zip
   */
  #showUnavailableState(zip) {
    const unavailableResult = this.#getModalRef('unavailableResult');
    const errorMessage = this.#getModalRef('errorMessage');

    if (errorMessage instanceof HTMLElement) {
      errorMessage.hidden = true;
    }

    this.#setUnavailableMessage(zip, 'loading');

    if (unavailableResult instanceof HTMLElement) {
      unavailableResult.hidden = false;
    }

    this.#lastCheckedZip = zip;
    void this.#loadAlternateStores(zip);
  }

  /**
   * @param {string} zip
   */
  async #loadAlternateStores(zip) {
    this.#abortAlternateStoresFetch();
    this.#resetAlternateStoresUi();

    const loading = this.#getModalRef('alternateStoresLoading');
    const empty = this.#getModalRef('alternateStoresEmpty');

    const locator = this.#getStoreLocator();
    if (!locator) {
      this.#setUnavailableMessage(zip, 'noAlternates');
      if (empty instanceof HTMLElement) empty.hidden = false;
      return;
    }

    const requestId = ++this.#alternateStoresRequestId;
    const abortController = new AbortController();
    this.#alternateStoresAbort = abortController;

    if (loading instanceof HTMLElement) loading.hidden = false;

    try {
      const result = await locator.findAlternateDeliveryStores(zip, {
        signal: abortController.signal,
        limit: 5,
      });

      if (requestId !== this.#alternateStoresRequestId) return;

      this.#renderAlternateStores(result, zip);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (requestId !== this.#alternateStoresRequestId) return;
      console.warn('[delivery-zip-modal] alternate stores fetch failed', error);
      this.#setUnavailableMessage(zip, 'noAlternates');
      if (empty instanceof HTMLElement) empty.hidden = false;
    } finally {
      if (requestId === this.#alternateStoresRequestId && loading instanceof HTMLElement) {
        loading.hidden = true;
      }
    }
  }

  /**
   * @param {AlternateStoreResult} result
   * @param {string} zip
   */
  #renderAlternateStores(result, zip) {
    const alternateResults = this.#getModalRef('alternateResults');
    const count = this.#getModalRef('alternateStoresCount');
    const list = this.#getModalRef('alternateStoresList');
    const empty = this.#getModalRef('alternateStoresEmpty');
    const dialog = this.#getDialog();
    const locator = this.#getStoreLocator();

    if (!(list instanceof HTMLElement) || !locator) {
      this.#setUnavailableMessage(zip, 'noAlternates');
      if (empty instanceof HTMLElement) empty.hidden = false;
      return;
    }

    list.replaceChildren();

    if (result.stores.length === 0) {
      this.#setUnavailableMessage(zip, 'noAlternates');
      if (empty instanceof HTMLElement) empty.hidden = false;
      return;
    }

    this.#setUnavailableMessage(zip, 'withAlternates');

    if (empty instanceof HTMLElement) empty.hidden = true;

    const translations = Theme.translations ?? {};
    const one = translations.store_locator_stores_found_one ?? '';
    const other = translations.store_locator_stores_found_other ?? '';
    const countTemplate = result.stores.length === 1 ? one : other;

    if (count instanceof HTMLElement) {
      count.textContent = countTemplate.replace(/\{\{\s*count\s*\}\}/g, String(result.stores.length));
      count.hidden = false;
    }

    const items = locator.renderAlternateDeliveryResults(result.stores, {
      distancesByStoreId: result.distancesByStoreId,
      origin: result.origin,
      searchQuery: zip,
      onSelectStore: (store, button) => {
        void this.#selectAlternateStore(store, button);
      },
    });

    for (const item of items) {
      list.appendChild(item);
    }

    if (alternateResults instanceof HTMLElement) {
      alternateResults.hidden = false;
    }

    dialog?.classList.add('delivery-zip-modal__dialog--wide');
  }

  /**
   * @param {import('@theme/store-locator-search').StoreLocatorStore} store
   * @param {HTMLButtonElement} button
   */
  async #selectAlternateStore(store, button) {
    const locator = this.#getStoreLocator();
    if (!locator) return;

    button.disabled = true;

    try {
      const result = await locator.selectStoreForPdp(store);
      if (!result?.ok) return;

      const availability = this.#getStoreProductAvailability();
      if (availability) {
        await availability.refreshInventory();
      }

      await this.checkZip();
    } finally {
      button.disabled = false;
    }
  }

  open(prefillZip = '') {
    const zipInput = this.#getModalRef('zipInput');
    const errorMessage = this.#getModalRef('errorMessage');

    this.#resetToForm();
    this.#setCheckLoading(false);

    if (zipInput instanceof HTMLInputElement) {
      zipInput.value = prefillZip;
    }

    if (errorMessage instanceof HTMLElement) {
      errorMessage.textContent = '';
      errorMessage.hidden = true;
    }

    const modal = document.getElementById('delivery-zip-modal');
    if (modal instanceof DialogComponent) {
      modal.showDialog();
      void this.#prefillZipInput(prefillZip, zipInput);
    }
  }

  /**
   * @param {string} prefillZip
   * @param {HTMLInputElement | null} zipInput
   */
  async #prefillZipInput(prefillZip, zipInput) {
    if (!(zipInput instanceof HTMLInputElement)) return;

    let zip = prefillZip.trim();
    if (!zip) {
      const cartAttributes = await fetchCartAttributes();
      zip = getDeliveryZipFromCartAttributes(cartAttributes);
    }

    zipInput.value = zip;

    this.#syncRemoveZipButton(zip);

    requestAnimationFrame(() => {
      zipInput.focus();
    });
  }

  removeZip = async (event) => {
    event?.preventDefault();

    const availability = this.#getStoreProductAvailability();
    if (availability && typeof availability.clearDeliveryZip === 'function') {
      await availability.clearDeliveryZip();
    } else {
      await patchCartAttributes(buildDeliveryZipPatch(''));
    }

    await this.#closeModal();
  };

  /**
   * @param {string} displayZip
   */
  async #persistDeliveryZip(displayZip) {
    await patchCartAttributes(buildDeliveryZipPatch(displayZip));
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const errorMessage = this.#getModalRef('errorMessage');
    if (!(errorMessage instanceof HTMLElement)) return;
    errorMessage.textContent = message;
    errorMessage.hidden = !message;
  }

  /**
   * @param {DeliveryZipCheckedDetail} detail
   */
  #dispatchDeliveryZipChecked(detail) {
    document.dispatchEvent(
      new CustomEvent(DELIVERY_ZIP_CHECKED_EVENT, {
        detail,
      })
    );
  }

  async #closeModal() {
    const modal = document.getElementById('delivery-zip-modal');
    if (modal instanceof DialogComponent) {
      await modal.closeDialog();
    }
  }

  /**
   * @param {string} rawInput
   */
  async #openLocatorWithQuery(rawInput) {
    await this.#closeModal();

    const locator = this.#getStoreLocator();
    locator?.openWithQuery?.(rawInput);
  }

  checkZip = async () => {
    const zipInput = this.#getModalRef('zipInput');
    if (!(zipInput instanceof HTMLInputElement)) return;

    const rawInput = zipInput.value.trim();
    const zip = normalizeZipDigits(rawInput);

    this.#showError('');
    this.#setCheckLoading(true);

    try {
      if (!zip) {
        await this.#openLocatorWithQuery(rawInput);
        return;
      }

      this.#hideUnavailableState();

      const [cartAttributes, catalog] = await Promise.all([
        fetchCartAttributes(),
        Promise.resolve(loadStoreCatalog()),
      ]);

      if (!hasStoreCartAttributes(cartAttributes)) {
        this.#showError('Select a store to check delivery.');
        return;
      }

      const store = findStoreFromCartAttributes(catalog, cartAttributes);
      if (!store) {
        this.#showError('Unable to find your selected store.');
        return;
      }

      const inServiceArea = isZipInStoreServiceArea(store, zip);
      const displayZip = normalizeServiceZip(zip) || zip;

      if (inServiceArea) {
        const availability = this.#getStoreProductAvailability();
        if (availability) {
          let status = availability.getInventoryStatus();
          if (status === 'loading') {
            await availability.refreshInventory();
            status = availability.getInventoryStatus();
          }

          this.#dispatchDeliveryZipChecked({ zip: displayZip, inServiceArea: true });
          await this.#persistDeliveryZip(displayZip);

          if (canAddToCartForDelivery(status)) {
            await this.#closeModal();
          }
          return;
        }

        this.#dispatchDeliveryZipChecked({ zip: displayZip, inServiceArea: true });
        await this.#persistDeliveryZip(displayZip);
        return;
      }

      this.#dispatchDeliveryZipChecked({ zip: displayZip, inServiceArea: false });
      await this.#persistDeliveryZip(displayZip);
      this.#showUnavailableState(displayZip);
    } finally {
      this.#setCheckLoading(false);
    }
  };

  /** @param {Event} event */
  handleSubmit = (event) => {
    event.preventDefault();
    void this.checkZip();
  };
}

if (!customElements.get('delivery-zip-modal')) {
  customElements.define('delivery-zip-modal', DeliveryZipModal);
}

export { DeliveryZipModal };
